import { Button, Group } from '@mantine/core';
import { ReactNode, useState } from 'react';

type Option<T> = {
  label: string;
  value: T;
};

interface BaseOptionsSwitcherProps<T> {
  options: readonly Option<T>[];

  renderOption?: (option: T) => ReactNode;
  onChanged: (option: T) => void;
}

type UncontrolledOptionsSwitcherProps<T> = BaseOptionsSwitcherProps<T> & {
  initialOption: T;
};

export function UncontrolledOptionsSwitcher<T>({
  options,
  initialOption,
  renderOption,
  onChanged,
}: UncontrolledOptionsSwitcherProps<T>) {
  const [selectedOption, setSelectedOption] = useState<Option<T>['value']>(initialOption);

  return (
    <>
      <Group gap='xs'>
        {options.map((option) => (
          <Button
            key={option.label}
            variant={selectedOption === option.value ? 'filled' : 'outline'}
            onClick={() => {
              setSelectedOption(option.value);
              onChanged(option.value);
            }}
          >
            {option.label}
          </Button>
        ))}
      </Group>
      {renderOption && renderOption(selectedOption)}
    </>
  );
}

interface ControlledOptionsSwitcherProps<T> extends BaseOptionsSwitcherProps<T> {
  value: T;
}

export function ControlledOptionsSwitcher<T>({
  options,
  value,
  renderOption,
  onChanged,
}: ControlledOptionsSwitcherProps<T>) {
  return (
    <>
      <Group gap='xs'>
        {options.map((option) => (
          <Button
            key={option.label}
            variant={value === option.value ? 'filled' : 'default'}
            onClick={() => {
              onChanged(option.value);
            }}
          >
            {option.label}
          </Button>
        ))}
      </Group>
      {renderOption && renderOption(value)}
    </>
  );
}
